import { FacetAccordion } from 'components/search/FacetAccordion/FacetAccordion';
import { useFacetsContext } from 'components/search/Facets/FacetsProvider';
import { sectionClasses } from 'components/search/QuickFilterPills/QuickFilterDrawerBody.classes';
import useMartyContext from 'hooks/useMartyContext';
import type { FacetDataType, FacetValueType, NavigationItem } from 'types/search';

interface QuickFilterSectionContentProps {
  section: NavigationItem;
  valueListClassName: string;
  /** Names the section's region when it renders without its header. */
  regionLabel?: string;
}

/** Renders one scoped section through FacetsDrawer's FacetAccordion pipeline and shared FacetsProvider actions. */
const QuickFilterSectionContent = ({ section, valueListClassName, regionLabel }: QuickFilterSectionContentProps) => {
  const { testId } = useMartyContext();
  const {
    state: { facets, autoComplete, isLoading },
    actions: { handleFacetSelect, trackFacetSearch }
  } = useFacetsContext();

  // Resolve the active facet and value indexes, then delegate selection to FacetsProvider as a Quick Filter Pill click.
  const handleValueSelect = (facet: FacetDataType, facetValue: FacetValueType) => {
    // Suppress facet updates while search results are loading, matching FacetsDrawer.
    if (isLoading) {
      return;
    }

    const selectedFacetGroupIndex = facets.findIndex(item => item.facetField === facet.facetField);
    const selectedFacetIndex = facet.values.findIndex(value => value.name === facetValue.name);

    handleFacetSelect({
      facetGroup: facet.facetField,
      facetName: facetValue.name,
      selectedFacetGroupIndex,
      selectedFacetIndex,
      isSelected: facetValue.selected || false,
      facetValue,
      facetClickSource: 'QUICK_FILTER_PILL'
    });
  };

  // Without a header, name the region directly instead of by Radix's unrendered trigger.
  const regionProps = regionLabel ? { 'aria-label': regionLabel, 'aria-labelledby': undefined } : {};

  return (
    <FacetAccordion.Content {...regionProps}>
      <FacetAccordion.AutoComplete onChange={event => trackFacetSearch(section, event.target.value)} />

      <FacetAccordion.ValueList
        data-test-id={testId(`quickFilterFacetValues-${section.facetField}`)}
        autoComplete={autoComplete}
        className={valueListClassName}
      >
        {({ facetValue, facet: activeFacet }) => (
          <FacetAccordion.Value
            data-test-id={testId(`quickFilterFacet-${activeFacet.facetField}-${facetValue.name}`)}
            facetValue={facetValue}
            onFacetSelect={() => handleValueSelect(activeFacet, facetValue)}
          />
        )}
      </FacetAccordion.ValueList>
    </FacetAccordion.Content>
  );
};

export interface QuickFilterDrawerBodyProps {
  sections: NavigationItem[];
  expandedSections: ReadonlySet<string>;
  onExpandedSectionsChange: (values: string[]) => void;
}

/** Renders one section directly or several with FacetsDrawer's controlled accordion pattern. */
export const QuickFilterDrawerBody = ({ sections, expandedSections, onExpandedSectionsChange }: QuickFilterDrawerBodyProps) => {
  const { testId } = useMartyContext();

  const [firstSection] = sections;
  // Skip body rendering when the pill has no scoped sections.
  if (!firstSection) {
    return null;
  }

  // Render one section open without a trigger, matching FacetsDrawer's scoped detail view.
  if (sections.length === 1) {
    return (
      <FacetAccordion.Root type="multiple" value={[firstSection.facetField]}>
        <FacetAccordion.Item value={firstSection.facetField} facet={firstSection} context="mobileQuickFilter" className={sectionClasses}>
          <QuickFilterSectionContent
            section={firstSection}
            valueListClassName="max-h-none overflow-visible"
            regionLabel={firstSection.facetFieldDisplayName}
          />
        </FacetAccordion.Item>
      </FacetAccordion.Root>
    );
  }

  return (
    <FacetAccordion.Root type="multiple" value={[...expandedSections]} onValueChange={onExpandedSectionsChange}>
      {sections.map(section => {
        const { facetField, facetFieldDisplayName } = section;

        return (
          <FacetAccordion.Item
            key={facetField}
            value={facetField}
            data-test-id={testId(`quickFilterSection-${facetFieldDisplayName}`)}
            facet={section}
            context="mobileQuickFilter"
            className={sectionClasses}
          >
            <FacetAccordion.Trigger data-test-id-facet-head-name={facetFieldDisplayName}>
              <FacetAccordion.DisplayName data-test-id={testId('quickFilterSectionHead')} />
              <FacetAccordion.SelectedValueNames data-test-id={testId('quickFilterSectionSelectedValues')} />
            </FacetAccordion.Trigger>

            <QuickFilterSectionContent section={section} valueListClassName="max-h-none" />
          </FacetAccordion.Item>
        );
      })}
    </FacetAccordion.Root>
  );
};
